// ClarityStack Patients List v2.1 — Referral source fallback
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Search, FlaskConical, ChevronRight, Building, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PatientsPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    phone: '',
    occupation: '',
    primary_concern: '',
    previous_therapy: '',
    medications: '',
    referral_source: '',
    referral_type: '',
    consent_ai_notes: false,
    consent_whatsapp: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, [searchQuery]);

  const fetchPatients = async () => {
    try {
      const url = searchQuery 
        ? `${API}/patients?search=${encodeURIComponent(searchQuery)}`
        : `${API}/patients`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPatients(data);
      }
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/patients`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          age: formData.age ? parseInt(formData.age) : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create patient');
      }

      const newPatient = await response.json();
      setPatients(prev => [newPatient, ...prev]);
      setDialogOpen(false);
      setFormData({
        name: '',
        email: '',
        age: '',
        phone: '',
        occupation: '',
        primary_concern: '',
        previous_therapy: '',
        medications: '',
        referral_source: '',
        referral_type: '',
        consent_ai_notes: false,
        consent_whatsapp: false
      });
      toast.success('Patient added successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getReferralIcon = (type) => {
    switch (type) {
      case 'clinic':
        return <Building className="h-3 w-3" />;
      case 'psychiatrist':
        return <User className="h-3 w-3" />;
      case 'patient':
        return <Users className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getReferralLabel = (type, source) => {
    if (!type && !source) return '-';
    if (type === 'clinic') return source || 'Clinic';
    if (type === 'psychiatrist') return source || 'Psychiatrist';
    if (type === 'patient') return source || 'Patient Referral';
    return source || 'Self';
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="patients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-foreground"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Patients
            </h1>
            <p className="text-muted-foreground mt-1">
              {patients.length} total patients
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-primary hover:bg-primary/90" data-testid="add-patient-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>
                  Add New Patient
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      required
                      data-testid="patient-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age">Age</Label>
                    <Input
                      id="age"
                      name="age"
                      type="number"
                      value={formData.age}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      data-testid="patient-age-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="rounded-xl"
                    required
                    data-testid="patient-email-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (WhatsApp)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      placeholder="+91 98765 43210"
                      data-testid="patient-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="occupation">Occupation</Label>
                    <Input
                      id="occupation"
                      name="occupation"
                      value={formData.occupation}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      placeholder="e.g., Software Engineer"
                      data-testid="patient-occupation-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="referral_type">Referral Type</Label>
                    <Select
                      value={formData.referral_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, referral_type: value }))}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self">Self</SelectItem>
                        <SelectItem value="clinic">Clinic</SelectItem>
                        <SelectItem value="psychiatrist">Psychiatrist</SelectItem>
                        <SelectItem value="patient">Patient Referral</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="referral_source">Referral Source</Label>
                    <Input
                      id="referral_source"
                      name="referral_source"
                      value={formData.referral_source}
                      onChange={handleInputChange}
                      className="rounded-xl"
                      placeholder="Name or details"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="primary_concern">Primary Concern</Label>
                  <Textarea
                    id="primary_concern"
                    name="primary_concern"
                    value={formData.primary_concern}
                    onChange={handleInputChange}
                    className="rounded-xl"
                    placeholder="What brings them to therapy?"
                    data-testid="patient-concern-input"
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-medium">Consent</Label>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consent_ai_notes"
                      checked={formData.consent_ai_notes}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, consent_ai_notes: checked }))
                      }
                      data-testid="consent-ai-checkbox"
                    />
                    <Label htmlFor="consent_ai_notes" className="text-sm text-muted-foreground">
                      Consent for AI-assisted notes
                    </Label>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consent_whatsapp"
                      checked={formData.consent_whatsapp}
                      onCheckedChange={(checked) => 
                        setFormData(prev => ({ ...prev, consent_whatsapp: checked }))
                      }
                      data-testid="consent-whatsapp-checkbox"
                    />
                    <Label htmlFor="consent_whatsapp" className="text-sm text-muted-foreground">
                      Consent for WhatsApp communication
                    </Label>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 rounded-xl"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary hover:bg-primary/90"
                    disabled={submitting}
                    data-testid="submit-patient-btn"
                  >
                    {submitting ? 'Adding...' : 'Add Patient'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-xl bg-white"
            data-testid="search-patients-input"
          />
        </div>

        {/* CRM Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-primary">Loading patients...</div>
          </div>
        ) : patients.length === 0 ? (
          <Card className="border-border">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                {searchQuery ? 'No patients found' : 'No patients yet'}
              </h3>
              <p className="text-muted-foreground text-center max-w-sm">
                {searchQuery 
                  ? 'Try adjusting your search query'
                  : 'Add your first patient or share your intake form link'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[250px]">Patient</TableHead>
                    <TableHead>Primary Concern</TableHead>
                    <TableHead>Referral</TableHead>
                    <TableHead className="w-[100px]">Consent</TableHead>
                    <TableHead className="w-[120px]">Added</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow 
                      key={patient.patient_id}
                      className={`cursor-pointer hover:bg-muted/50 ${patient.is_demo ? 'bg-amber-50/30' : ''}`}
                      onClick={() => navigate(`/patients/${patient.patient_id}`)}
                      data-testid={`patient-row-${patient.patient_id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${patient.is_demo ? 'bg-amber-100' : 'bg-secondary/30'}`}>
                            <span className={`text-sm font-medium ${patient.is_demo ? 'text-amber-700' : 'text-primary'}`}>
                              {patient.name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground truncate">{patient.name}</p>
                              {patient.is_demo && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs py-0 flex-shrink-0">
                                  <FlaskConical className="h-3 w-3 mr-1" />
                                  Demo
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {patient.occupation || (patient.age ? `${patient.age} years` : patient.email)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-foreground line-clamp-2 max-w-[200px]">
                          {patient.primary_concern || '-'}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          {getReferralIcon(patient.referral_type)}
                          <span className="text-muted-foreground">
                            {getReferralLabel(patient.referral_type, patient.referral_source || patient.source_of_lead)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {patient.consent_ai_notes && (
                            <Badge variant="outline" className="text-xs py-0 badge-confirmed">AI</Badge>
                          )}
                          {patient.consent_whatsapp && (
                            <Badge variant="outline" className="text-xs py-0 badge-confirmed">WA</Badge>
                          )}
                          {!patient.consent_ai_notes && !patient.consent_whatsapp && (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(patient.created_at), 'MMM d, yyyy')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default PatientsPage;
